import './styles.scss';
import React, {Dispatch, useEffect, useState} from "react";

/* types, constants, utils */
import * as Constants from "../../../constants";
import Utils from "../../../utils";

/* bootstrap */
import {Accordion, Nav} from "react-bootstrap";

/* Redux */
import {ISavedState} from "../../../types/redux/saved";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {useQuery} from "react-query";
import SearchService from "../../../api/search";
import {AnyAction} from "@reduxjs/toolkit";
import {setPossibleDuplicates} from "../../../redux/slices/viewer-slice";
import usePlatform from "../../../hooks/usePlatform";
import {IViewerState} from "../../../types/redux/viewer";

interface IProps {
  fileType: string | undefined;
  setActiveTool(key: string): void;
}

const ViewerTools = (props: IProps):JSX.Element => {
  const {
    fileType,
    setActiveTool
  } = props;

  /* redux */
  const viewerState: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer);
  const saveState: ISavedState = useSelector<RootState, ISavedState>(state => state.saved);
  const dispatch:Dispatch<AnyAction> = useDispatch();

  /* state */
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [initialFetch, setInitialFetch] = useState<boolean>(true);
  const [signature, setSignature] = useState<string | null>(null);
  const [id, setID] = useState<string | null>(null);

  /* local */
  const hasBates: boolean = (viewerState.viewerRecord && viewerState.viewerRecord.bn);
  const isDesktop = usePlatform().isDesktop;

  const {data, isSuccess, refetch, isRefetching, } = useQuery<any, Error> (
    ['duplicates-'+ signature + id],
    async ({signal}) => {
      return await SearchService.solrDuplicatesQuery(viewerState.viewerRecord.signature, viewerState.viewerRecord.id, signal)
    },
    {
      staleTime: Infinity,
      enabled: false
    }
  );

  useEffect(()=> {
    if (data) {
      const docs: any[] = data.response.docs;
      dispatch(setPossibleDuplicates(docs));
      setHasDuplicates(docs.length > 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data])

  useEffect(()=>{
    if (!viewerState.viewerRecord) return;

    setIsSaved(saveState.documents.find(savedDocument => savedDocument.documentId === viewerState.viewerRecord.id) !== undefined);
    setID(viewerState.viewerRecord.id);
    setSignature(viewerState.viewerRecord.signature);
  }, [saveState.documents, viewerState.viewerRecord])

  useEffect(()=>{
    if (initialFetch && viewerState.viewerRecord && viewerState.viewerRecord.signature && !isRefetching) {
      setTimeout(()=>{
        setInitialFetch(false)
        refetch();
      }, 500) // temp: race condition with redux
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.viewerRecord])

  const handleSelect = (key: string | null): void => {
    if (!key) return;
    setActiveTool(key)
  }

  const navItem = (key: string, label: string, icon: string | null, disabled: boolean = false): JSX.Element =>{
    return (
      <Nav.Item >
        <Nav.Link
          disabled={(document === null) || disabled}
          className={'text-black ps-3 text-normal' +
            ((document === null) || disabled ? ' text-black-50': '') +
            ((viewerState.recordTool === key) ? ' active': '')}
          eventKey={key}
        >
          {icon && <i className={'bi me-1 ' + icon}/>}
          <span className={(!icon? 'ms-4': '')}>{label}</span>
        </Nav.Link>
      </Nav.Item>
    )
  }

  const recordsAccordion = ():JSX.Element=>{
    return (
      <>
        <Accordion className={'accordion-flush open'} defaultActiveKey={['0']} alwaysOpen>
        <Accordion.Item eventKey="0" className={'border-0'} >
          <Accordion.Header>
            <i className={'bi me-2 bi-files'}/>
            <span>{Utils.toTitleCase(Constants.RECORDS)}</span>
          </Accordion.Header>

          <Accordion.Body className={'p-0'}>
            <Nav className={'d-block'} activeKey={viewerState.recordTool}>
              {navItem(Constants.SEARCH_RESULTS, Utils.toTitleCase(Constants.SEARCH_RESULTS), null)}
              {navItem(Constants.BROWSE, Utils.toTitleCase(Constants.BROWSE), null)}
              {hasBates && navItem(Constants.BATES, Utils.toTitleCase(Constants.BATES), null)}
              {navItem(Constants.MORE_LIKE_THIS, Utils.toTitleCase(Constants.MORE_LIKE_THIS), null)}
              {hasDuplicates && navItem(Constants.POSSIBLE_DUPLICATES, Utils.toTitleCase(Constants.POSSIBLE_DUPLICATES), null)}
            </Nav>
          </Accordion.Body>
        </Accordion.Item>
        </Accordion>
      </>)
  }

  return (
    <div className={'viewer-tools h-100 border-end py-1'}>
      <Nav
        className={'flex-column'}
        onSelect={key => handleSelect(key)}
      >
        {(!isDesktop) && navItem(Constants.DOCUMENT, Utils.toTitleCase(Constants.DOCUMENT), 'bi-file-text')}
        {navItem(Constants.METADATA, Utils.toTitleCase(Constants.METADATA), 'bi-file-earmark-code')}
        {(!isDesktop) && navItem(Constants.RECORDS, Utils.toTitleCase(Constants.RECORDS), 'bi-files')}
        {isDesktop && <div>{recordsAccordion()}</div>}
        {navItem(Constants.CITE, Utils.toTitleCase(Constants.CITE), 'bi-quote')}
        {navItem(Constants.TAGS_NOTES, Utils.toTitleCase(Constants.TAGS_NOTES), isSaved? 'bi-bookmark-x' : 'bi-bookmark')}
        {navItem(Constants.DOWNLOAD, Utils.toTitleCase(Constants.DOWNLOAD), 'bi-download', (fileType === Constants.VIDEO))}
        {navItem(Constants.SHARE, Utils.toTitleCase(Constants.SHARE), 'bi-share')}
        {navItem(Constants.REPORT_ISSUES, Utils.toTitleCase(Constants.REPORT_ISSUES), 'bi-exclamation-circle')}
      </Nav>
    </div>
  )
}

export default ViewerTools;
